import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, Send, CheckCircle, XCircle, Loader } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_ORDER = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].reduce((a,d,i)=>({...a,[d]:i}),{});
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("dynamo_token")}` });

// "09:30" → "9:30am"  (always shows minutes)
function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

// "2026-08-07" → "7th August 2026"
function fmtDateUK(dateStr) {
  if (!dateStr) return "";
  const [y, mo, d] = dateStr.split("-").map(Number);
  const date = new Date(y, mo - 1, d);
  const day = date.getDate();
  const s = (day >= 11 && day <= 13) ? "th"
    : ({ 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th");
  const months = ["January","February","March","April","May","June",
                  "July","August","September","October","November","December"];
  return `${day}${s} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

// "2026-08-07" → "7th August"  (no year)
function fmtDateUKShort(dateStr) {
  if (!dateStr) return "";
  const [y, mo, d] = dateStr.split("-").map(Number);
  const date = new Date(y, mo - 1, d);
  const day = date.getDate();
  const s = (day >= 11 && day <= 13) ? "th"
    : ({ 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th");
  const months = ["January","February","March","April","May","June",
                  "July","August","September","October","November","December"];
  return `${day}${s} ${months[date.getMonth()]}`;
}

function fillTemplate(tmpl, data) {
  let out = tmpl;
  for (const [k, v] of Object.entries(data)) {
    out = out.replaceAll(`{{${k}}}`, v);
  }
  return out;
}

function calcDuration(start, end) {
  if (!start || !end) return "1 hour";
  const [h1, m1] = start.split(":").map(Number);
  const [h2, m2] = end.split(":").map(Number);
  const total = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (total <= 0) return "1 hour";
  const h = Math.floor(total / 60);
  const m = total % 60;
  const parts = [];
  if (h) parts.push(`${h} hour${h > 1 ? "s" : ""}`);
  if (m) parts.push(`${m} minute${m > 1 ? "s" : ""}`);
  return parts.join(" ");
}

export default function SendInvitesModal({ open, onClose, selectedEntries, wlSheet, onSent }) {
  const [step, setStep] = useState(1);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [tasterDate, setTasterDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [template, setTemplate] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedClass(null);
    setTasterDate("");
    setStartDate("");
    setResults(null);
    setPreviewIdx(0);
    fetchClasses();
    fetchTemplate();
  }, [open]);

  async function fetchClasses() {
    setLoadingClasses(true);
    try {
      const { data } = await axios.get(`${API}/classes`, { headers: auth() });
      const sorted = (data.data || []).sort((a, b) => {
        const dd = (DAY_ORDER[a.day] ?? 99) - (DAY_ORDER[b.day] ?? 99);
        return dd !== 0 ? dd : (a.start_time || "").localeCompare(b.start_time || "");
      });
      setClasses(sorted);
    } catch {
      toast.error("Failed to load classes");
    } finally {
      setLoadingClasses(false);
    }
  }

  async function fetchTemplate() {
    try {
      const { data } = await axios.get(`${API}/email-templates/taster_invite`, { headers: auth() });
      setTemplate(data);
    } catch {
      setTemplate({ subject: "", body: "" });
    }
  }

  function buildPreviewData(entry) {
    if (!selectedClass || !entry) return {};
    const parentParts = (entry.adult_full_name || "").split(" ");
    const parentFirst = parentParts[0] || entry.adult_full_name;
    return {
      // New canonical placeholders
      "User First Name":          parentFirst,
      "Beneficiary First Name":   entry.child_first_name,
      accept_url:                 "#",
      decline_url:                "#",
      // Legacy placeholders (kept for saved templates)
      child_first_name:           entry.child_first_name,
      child_last_name:            entry.child_last_name,
      parent_name:                entry.adult_full_name,
      parent_first_name:          parentFirst,
      class_name:                 selectedClass.category || "Recreational",
      class_day:                  (selectedClass.day || "").trim(),
      taster_date:                fmtDateUK(tasterDate),
      taster_date_short:          fmtDateUKShort(tasterDate),
      taster_time:                fmt12(selectedClass.start_time),
      start_date:                 fmtDateUK(startDate),
      start_date_short:           fmtDateUKShort(startDate),
      start_time:                 fmt12(selectedClass.start_time),
      class_price:                selectedClass.price ? Number(selectedClass.price).toFixed(2) : "0.00",
      class_duration:             calcDuration(selectedClass.start_time, selectedClass.end_time),
    };
  }

  async function handleSend() {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/invites/send`, {
        selected_rows: selectedEntries,
        class_id:      selectedClass.id,
        taster_date:   tasterDate,
        start_date:    startDate,
        wl_sheet:      wlSheet,
        base_url:      window.location.origin,
      }, { headers: auth() });
      setResults(data);
      setStep(4);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  const canNext1 = !!selectedClass;
  const canNext2 = tasterDate && startDate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        data-testid="send-invites-modal"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Send Taster Invites
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {selectedEntries.length} {selectedEntries.length === 1 ? "child" : "children"} selected
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Step pills */}
            <div className="hidden sm:flex items-center gap-1.5">
              {["Class", "Dates", "Preview"].map((label, i) => (
                <div key={label} className="flex items-center gap-1.5">
                  <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                    ${step > i + 1 ? "bg-emerald-100 text-emerald-700"
                      : step === i + 1 ? "bg-[#1656a4] text-white"
                      : "bg-slate-100 text-slate-400"}`}>
                    <span>{i + 1}</span>
                    <span>{label}</span>
                  </div>
                  {i < 2 && <div className="w-4 h-px bg-slate-200" />}
                </div>
              ))}
            </div>
            <button data-testid="close-modal-button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-md transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── Step 1: Select class ──────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-3">Select the class to invite for:</p>
              {loadingClasses ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}
                </div>
              ) : classes.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">No classes found. Add a class first.</p>
              ) : (
                <div className="space-y-2">
                  {classes.map(cls => (
                    <button
                      key={cls.id}
                      data-testid={`select-class-${cls.id}`}
                      onClick={() => setSelectedClass(cls)}
                      className={`w-full text-left px-4 py-3.5 rounded-lg border-2 transition-all
                        ${selectedClass?.id === cls.id
                          ? "border-[#1656a4] bg-blue-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {cls.category || "Recreational"}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {cls.day} · {fmt12(cls.start_time)} – {fmt12(cls.end_time)} · {cls.spaces} taster spaces
                          </p>
                        </div>
                        {selectedClass?.id === cls.id && (
                          <CheckCircle size={18} className="text-[#1656a4] shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Select dates ──────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-sm font-medium text-slate-700">
                Class: <span className="text-[#1656a4]">{selectedClass?.category} — {selectedClass?.day} {fmt12(selectedClass?.start_time)}</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Taster session date
                  </label>
                  <input
                    data-testid="taster-date-input"
                    type="date"
                    value={tasterDate}
                    onChange={e => setTasterDate(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                  />
                  {tasterDate && (
                    <p className="text-xs text-slate-500 mt-1">{fmtDateUK(tasterDate)} at {fmt12(selectedClass?.start_time)}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Start date after taster
                  </label>
                  <input
                    data-testid="start-date-input"
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-md
                               focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                  />
                  {startDate && (
                    <p className="text-xs text-slate-500 mt-1">{fmtDateUK(startDate)} at {fmt12(selectedClass?.start_time)}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Preview ───────────────────────────────────────────── */}
          {step === 3 && (
            <div>
              {/* Recipient tabs */}
              <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
                {selectedEntries.map((entry, i) => (
                  <button
                    key={i}
                    data-testid={`preview-tab-${i}`}
                    onClick={() => setPreviewIdx(i)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors shrink-0
                      ${previewIdx === i
                        ? "bg-[#1656a4] text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    {entry.child_first_name} {entry.child_last_name.charAt(0)}.
                  </button>
                ))}
              </div>

              {/* Subject preview */}
              <div className="mb-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400 mb-0.5">Subject</p>
                <p className="text-sm font-medium text-slate-800">
                  {template ? fillTemplate(template.subject, buildPreviewData(selectedEntries[previewIdx])) : ""}
                </p>
              </div>

              {/* To */}
              <div className="mb-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400 mb-0.5">To</p>
                <p className="text-sm text-slate-700">{selectedEntries[previewIdx]?.adult_email}</p>
              </div>

              {/* Email body */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                  <p className="text-xs text-slate-400">Email preview</p>
                </div>
                <div
                  data-testid="email-preview"
                  className="p-4 overflow-y-auto max-h-64 text-sm"
                  dangerouslySetInnerHTML={{
                    __html: template
                      ? fillTemplate(template.body, buildPreviewData(selectedEntries[previewIdx]))
                      : "<p>Loading template…</p>"
                  }}
                />
              </div>

            
            </div>
          )}

          {/* ── Step 4: Results ───────────────────────────────────────────── */}
          {step === 4 && results && (
            <div>
              <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{results.emails_sent}</p>
                  <p className="text-xs text-slate-500">Emails sent</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{results.sheets_updated}</p>
                  <p className="text-xs text-slate-500">WL highlighted</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{results.invites_logged}</p>
                  <p className="text-xs text-slate-500">Invites logged</p>
                </div>
              </div>
              <div className="space-y-2">
                {results.results.map((r, i) => (
                  <div key={i} data-testid={`result-row-${i}`}
                    className="flex items-start gap-3 px-4 py-3 rounded-lg border border-slate-200">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{r.child_name}</p>
                      <p className="text-xs text-slate-400">{r.email}</p>
                      {r.error && <p className="text-xs text-red-500 mt-0.5">{r.error}</p>}
                    </div>
                    <div className="flex items-center gap-2 text-xs shrink-0">
                      <span title="Email" className={r.email_sent ? "text-emerald-600" : "text-slate-300"}>
                        {r.email_sent ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      </span>
                      <span title="WL updated" className={r.sheet_updated ? "text-emerald-600" : "text-slate-300"}>
                        {r.sheet_updated ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      </span>
                      <span title="Logged to invites" className={r.invites_logged ? "text-emerald-600" : "text-slate-300"}>
                        {r.invites_logged ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => step > 1 && step < 4 ? setStep(s => s - 1) : onClose}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 px-3 py-2 rounded-md transition-colors"
          >
            {step < 4 && step > 1 && <ChevronLeft size={15} />}
            {step === 4 ? "Close" : step === 1 ? "Cancel" : "Back"}
          </button>

          <div className="flex items-center gap-2">
            {step < 3 && (
              <button
                data-testid="next-step-button"
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 ? !canNext1 : !canNext2}
                className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687] text-white
                           text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
              >
                Next
                <ChevronRight size={15} />
              </button>
            )}
            {step === 3 && (
              <button
                data-testid="confirm-send-button"
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687] text-white
                           text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
              >
                {sending ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? "Sending…" : `Send ${selectedEntries.length} Invite${selectedEntries.length !== 1 ? "s" : ""}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
